import { useMemo } from 'react';

/**
 * FolderTreeDiff — side-by-side view of current vs. proposed folder structure.
 *
 * Props:
 *   currentTree    — FolderTreeResponse from /folder-tree  (left, read-only)
 *   proposedTree   — FolderTreeResponse emitted inline in the plan_ready event  (right, preview)
 *   documentCounts — Map(path → documents in the folder): the plan files documents, so the left
 *                    side counts documents too (null = the server tree's counts)
 *   filesThatStay  — Map(path → files the plan never moves): a folder holding one is kept, not removed
 *   documentsThatStay — Map(path → documents the plan leaves there: filed by hand, or a second appearance)
 */
export default function FolderTreeDiff({ currentTree, proposedTree, scope, documentCounts = null, filesThatStay = null, documentsThatStay = null }) {
  const currentPaths = useMemo(() => extractPaths(currentTree?.tree || []), [currentTree]);
  const proposedPaths = useMemo(() => extractPaths(proposedTree?.tree || []), [proposedTree]);
  const currentCounts = useMemo(
    () => documentCounts || extractCounts(currentTree?.tree || []),
    [currentTree, documentCounts],
  );

  return (
    <div className="ftd-container">
      <div className="ftd-col">
        <div className="ftd-col-header">Current</div>
        <div className="ftd-tree-scroll">
          {(currentTree?.unfiled_count > 0) && (
            <div className="ftd-row ftd-unchanged">
              <span className="ftd-icon">📄</span>
              <span className="ftd-label">Unfiled</span>
              <span className="ftd-count">{currentTree.unfiled_count}</span>
            </div>
          )}
          {(currentTree?.tree || []).map(node => (
            <DiffNode key={node.path} node={node} knownPaths={proposedPaths} currentCounts={currentCounts} filesThatStay={filesThatStay} documentsThatStay={documentsThatStay} side="current" depth={0} scope={scope} />
          ))}
          {(!currentTree?.tree?.length && !currentTree?.unfiled_count) && (
            <div className="ftd-empty">No folders yet</div>
          )}
        </div>
      </div>

      <div className="ftd-divider" />

      <div className="ftd-col">
        <div className="ftd-col-header">Proposed</div>
        <div className="ftd-tree-scroll">
          {(proposedTree?.tree || []).map(node => (
            <DiffNode key={node.path} node={node} knownPaths={currentPaths} currentCounts={currentCounts} side="proposed" depth={0} />
          ))}
          {!proposedTree?.tree?.length && (
            <div className="ftd-empty">Plan not ready yet…</div>
          )}
        </div>
      </div>
    </div>
  );
}

function DiffNode({ node, knownPaths, currentCounts, filesThatStay, documentsThatStay, side, depth, scope }) {
  const isNew = side === 'proposed' && !knownPaths.has(node.path);
  const leftOut = side === 'current' && !knownPaths.has(node.path) && scope !== 'unfiled';
  const keeps = leftOut ? (filesThatStay?.get(node.path) || 0) : 0;          // a file that is never moved lives here
  const keepsDocs = leftOut ? (documentsThatStay?.get(node.path) || 0) : 0;  // so do documents the plan leaves alone
  const isRemoved = leftOut && !keeps && !keepsDocs;
  const count = side === 'current' && currentCounts ? (currentCounts.get(node.path) ?? 0) : node.doc_count;
  const delta = side === 'proposed' && !isNew && currentCounts
    ? (node.doc_count || 0) - (currentCounts.get(node.path) ?? 0)
    : 0;
  const isTarget = delta > 0;

  const rowClass = [
    'ftd-row',
    isNew ? 'ftd-added' : isRemoved ? 'ftd-removed' : isTarget ? 'ftd-target' : 'ftd-unchanged',
  ].join(' ');

  return (
    <>
      <div className={rowClass} style={{ paddingLeft: 8 + depth * 14 }}>
        <span className="ftd-icon">{node.children?.length ? '📂' : '📁'}</span>
        <span className="ftd-label">{node.name}</span>
        {side === 'proposed' && node.holds && (
          <span className="ft-holds" data-testid="ftd-holds" title={`New documents of these kinds will file themselves here: ${node.holds}`}>✦</span>
        )}
        <span className="ftd-count">{count}</span>
        {isNew && <span className="ftd-badge ftd-badge-new">new</span>}
        {isRemoved && <span className="ftd-badge ftd-badge-removed">removed</span>}
        {keeps > 0 && (
          <span className="ftd-badge ftd-badge-keeps" data-testid="ftd-keeps-file" title="A file that was split into documents stays in this folder; only its documents are filed elsewhere">
            keeps {keeps} file{keeps === 1 ? '' : 's'}
          </span>
        )}
        {keepsDocs > 0 && (
          <span className="ftd-badge ftd-badge-keeps" data-testid="ftd-keeps-documents" title="Documents you filed here by hand, that also appear here, or whose kind Arc could not tell are not moved by this plan">
            keeps {keepsDocs} document{keepsDocs === 1 ? '' : 's'}
          </span>
        )}
        {isTarget && <span className="ftd-badge ftd-badge-target">+{delta}</span>}
      </div>
      {(node.children || []).map(child => (
        <DiffNode key={child.path} node={child} knownPaths={knownPaths} currentCounts={currentCounts} filesThatStay={filesThatStay} documentsThatStay={documentsThatStay} side={side} depth={depth + 1} scope={scope} />
      ))}
    </>
  );
}

function extractPaths(nodes, set = new Set()) {
  for (const node of nodes) {
    set.add(node.path);
    if (node.children?.length) extractPaths(node.children, set);
  }
  return set;
}

function extractCounts(nodes, map = new Map()) {
  for (const node of nodes) {
    map.set(node.path, node.doc_count || 0);
    if (node.children?.length) extractCounts(node.children, map);
  }
  return map;
}
